import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';

const ExamReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduxResults = useSelector((state) => state.exam.results);
  const results = location.state?.results || reduxResults;
  const startIndex = location.state?.startIndex || 0;
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  // Helper to bold answer letters (e.g., "A.", "B.")
  const formatExplanation = (text) => {
    if (!text) return '';
    return text.replace(/([A-F]\.|\s[A-F]\s–)/g, '<strong>$&</strong>');
  };

  if (!results || !Array.isArray(results) || results.length === 0) {
    return (
      <Container className="mt-5 text-center">
        <p>No results to review. Please complete an exam first.</p>
        <Button variant="outline-primary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const current = results[currentIndex];
  const total = results.length;
  const maxScore = current.mode === 'MC' ? 1 : current.correct_answer.split(',').length;
  const scoringRule = current.mode === 'MC' ? '0/1 Scoring' : 'Right minus wrong (min 0)';

  const handleNext = () => {
    if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1);
  };
  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  return (
    <Container className="mt-4" style={{ maxWidth: '1200px', marginLeft: '20px' }}>
      <h2 className="mb-3">Review Answers</h2>
      <p className="text-muted mb-4">Question {currentIndex + 1} of {total}</p>

      <Row>
        {/* Left column: question, options, stats */}
        <Col md={7}>
          <div className="mb-4">
            <p className="fw-bold mb-3">{current.question_text}</p>
            <div>
              {['A', 'B', 'C', 'D', 'E', 'F'].map(opt => {
                const optText = current.options?.[opt];
                if (!optText) return null;
                const isSelected = current.selected?.split(',').includes(opt);
                const isCorrect = current.correct_answer.split(',').includes(opt);

                const symbol = isCorrect ? '✓' : '✗';
                const symbolColor = isCorrect ? 'green' : 'red';
                const textColor = isCorrect ? 'green' : (isSelected ? 'red' : 'inherit');

                return (
                  <div key={opt} className="mb-2">
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                      <span style={{ width: 20, color: symbolColor, fontWeight: 'bold', marginRight: 8 }}>
                        {symbol}
                      </span>
                      <input
                        type={current.mode === 'MC' ? 'radio' : 'checkbox'}
                        checked={isSelected}
                        disabled
                        style={{ marginRight: 8 }}
                      />
                      <span style={{ color: textColor }}>
                        {opt}. {optText}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Statistics table */}
          <div className="mb-4 p-3 bg-light rounded">
            <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
              <span className="fw-bold">Scored/Max</span>
              <span className="fw-bold">Scoring Rule</span>
              <span className="fw-bold">Time Spent</span>
              <span className="fw-bold">Answered Correctly</span>
            </div>
            <div className="d-flex justify-content-between">
              <span>{current.score}/{maxScore}</span>
              <span>{scoringRule}</span>
              <span>N/A</span>
              <span>N/A</span>
            </div>
          </div>
        </Col>

        {/* Right column: full explanation with bolded answer letters */}
        <Col md={5}>
          <div
            className="p-4 bg-light rounded shadow-sm"
            style={{ maxHeight: '70vh', overflowY: 'auto' }}
          >
            <h5 className="mb-3">Explanation</h5>
            <div
              style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: formatExplanation(current.explanation) }}
            />
          </div>
        </Col>
      </Row>

      {/* Bottom navigation row */}
      <div className="mt-4 d-flex justify-content-between align-items-center">
        <div>
          <Button variant="outline-primary" onClick={handlePrev} disabled={currentIndex === 0} className="me-2">
            ← Previous
          </Button>
          <Button variant="primary" onClick={handleNext} disabled={currentIndex === total - 1}>
            Next →
          </Button>
        </div>
        <div>
          <Button variant="danger" onClick={() => navigate(-1)} className="me-2">
            ← Back to Report
          </Button>
          <Button variant="outline-primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default ExamReview;